// Launcher's OWN copy of the merge-field vocabulary and resolver. Launcher is
// the working production send path; Playbooks owns and evolves
// `mini-apps/_shared/launcher-copy-tokens.ts`, which has already diverged (a
// `Company Name` row, an `emptyFallback` column, Clay-owned token semantics).
//
// FROZEN here, at pre-PR-#1465 behavior:
//   - the vocabulary snapshot: `SENDER_TOKENS`, `CHANNELS`,
//     `COPY_VARIABLE_FIELDS`, and the `CopyVariableField
//     { label; variable; object }` shape (no `emptyFallback`, no
//     `Company Name`);
//   - the interpolation semantics: an unknown token stays LITERAL (re-braced
//     from the trimmed key), a known-but-empty token renders as `''`, and
//     there are no per-token fallbacks and no Clay-owned token state.
// Pinned by `__tests__/copy-tokens-pin.test.ts`, which also pins the SERVER
// path in `api.ts` — it blanks unknown tokens instead. The two differ on
// purpose; the pin test is not a proposal to reconcile them.
//
// DELIBERATELY STILL SHARED with `_shared` (contracts, not semantics):
//   - the `CampaignMember` type, imported below from
//     `_shared/launcher-campaign.types`;
//   - `newDispatchLogId` (`_shared/dispatch-log-id`), imported by `api.ts`.
//
// Do not re-unify the vocabulary or the resolver without a product decision —
// `_shared`'s picker already offers tokens this app's send cannot fill.
//
// Contents: outreach channels, sender tokens, the merge-token field list, and
// client-side template interpolation over a campaign member.
//
// Frontend-safe: no zod at runtime, no core imports.
import type { CampaignMember } from '../_shared/launcher-campaign.types';

// Inferred from the wire schemas in `./wire-schemas.ts` (type-only import: erased,
// so this file still ships no zod).
import type { SenderValue, Channel } from './wire-schemas';
export type { SenderValue, Channel };

export const SENDER_TOKENS = [
  { value: 'gtme_owner_email' as const,   label: 'GTME email' },
  { value: 'gs_owner_email' as const,     label: 'GS email' },
  { value: 'claydr_email' as const,       label: 'ClayDR email' },
];

export const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'gong_engage',   label: 'Gong Engage' },
  { value: 'inflection',    label: 'Inflection' },
  { value: 'slack_copilot', label: 'Terra Co-Pilot' },
];

export interface CopyVariableField { label: string; variable: string; object: string; }

export const COPY_VARIABLE_FIELDS: CopyVariableField[] = [
  // Contact
  { object: 'Contact', label: 'First Name',       variable: '{{First Name}}' },
  { object: 'Contact', label: 'Last Name',        variable: '{{Last Name}}' },
  { object: 'Contact', label: 'Full Name',        variable: '{{Full Name}}' },
  { object: 'Contact', label: 'Email',            variable: '{{Email}}' },
  { object: 'Contact', label: 'Title',            variable: '{{Title}}' },
  { object: 'Contact', label: 'Campaign Status',  variable: '{{Campaign Status}}' },
  { object: 'Contact', label: 'Owner Role',       variable: '{{Owner Role}}' },
  { object: 'Contact', label: 'Fit Score',        variable: '{{Fit Score}}' },
  // Account
  { object: 'Account', label: 'Account Name',        variable: '{{Account Name}}' },
  { object: 'Account', label: 'Sales Tier',           variable: '{{Sales Tier}}' },
  { object: 'Account', label: 'GTME Owner Name',       variable: '{{GTME Owner Name}}' },
  { object: 'Account', label: 'GTME Owner First Name', variable: '{{GTME Owner First Name}}' },
  { object: 'Account', label: 'GTME Owner Email',     variable: '{{GTME Owner Email}}' },
  { object: 'Account', label: 'GTME Meeting Link',    variable: '{{GTME Meeting Link}}' },
  { object: 'Account', label: 'GS Owner Name',        variable: '{{GS Owner Name}}' },
  { object: 'Account', label: 'GS Owner First Name',  variable: '{{GS Owner First Name}}' },
  { object: 'Account', label: 'GS Owner Email',       variable: '{{GS Owner Email}}' },
  { object: 'Account', label: 'GS Meeting Link',      variable: '{{GS Meeting Link}}' },
  { object: 'Account', label: 'ClayDR Name',          variable: '{{ClayDR Name}}' },
  { object: 'Account', label: 'ClayDR First Name',    variable: '{{ClayDR First Name}}' },
  { object: 'Account', label: 'ClayDR Email',         variable: '{{ClayDR Email}}' },
  { object: 'Account', label: 'ClayDR Meeting Link',  variable: '{{ClayDR Meeting Link}}' },
  // Scanner (primary met with)
  { object: 'Account', label: 'Scanner First Name',  variable: '{{Scanner First Name}}' },
  { object: 'Account', label: 'Scanner Email',        variable: '{{Scanner Email}}' },
  // Campaign
  { object: 'Campaign', label: 'Event Name',          variable: '{{Event Name}}' },
  { object: 'Campaign', label: 'Event Date',          variable: '{{Event Date}}' },
  { object: 'Campaign', label: 'Campaign Type',       variable: '{{Campaign Type}}' },
  // Sender
  { object: 'Sender', label: 'Sender First Name',     variable: '{{Sender First Name}}' },
  { object: 'Sender', label: 'Sender Email',          variable: '{{Sender Email}}' },
  { object: 'Sender', label: 'Sender Meeting Link',   variable: '{{Sender Meeting Link}}' },
  // Computed (fallback)
  { object: 'Account', label: 'Primary Seller Link',  variable: '{{Primary Seller Link}}' },
];

// ── Client-side template interpolation ───────────────────────────────────────

export function resolveSenderEmail(sender: SenderValue | null, member: CampaignMember): string | null {
  if (!sender) return null;
  if (sender.type === 'token') {
    const map: Record<string, string | null> = {
      gtme_owner_email:    member.gtme_owner_email ?? null,
      gs_owner_email:      member.gs_owner_email ?? null,
      claydr_email:        member.claydr_email ?? null,
      gtme_meeting_link:   member.gtme_meeting_link ?? null,
      gs_meeting_link:     member.gs_meeting_link ?? null,
      claydr_meeting_link: member.claydr_meeting_link ?? null,
    };
    return map[sender.token ?? ''] ?? null;
  }
  return sender.email ?? null;
}

export function resolveSenderFirstName(sender: SenderValue | null, member: CampaignMember): string {
  if (!sender) return '';
  if (sender.type === 'token') {
    const map: Record<string, string> = {
      gtme_owner_email: member.gtme_owner_first_name ?? member.gtme_owner_name?.split(' ')[0] ?? '',
      gs_owner_email:   member.gs_owner_first_name   ?? member.gs_owner_name?.split(' ')[0]   ?? '',
      claydr_email:     member.claydr_first_name      ?? member.claydr_name?.split(' ')[0]     ?? '',
    };
    return map[sender.token ?? ''] ?? '';
  }
  const local = (sender.email ?? '').split('@')[0];
  const first = local.split('.')[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

export function resolveSenderMeetingLink(sender: SenderValue | null, member: CampaignMember): string {
  if (!sender || sender.type !== 'token') return '';
  const map: Record<string, string> = {
    gtme_owner_email: member.gtme_meeting_link ?? '',
    gs_owner_email:   member.gs_meeting_link   ?? '',
    claydr_email:     member.claydr_meeting_link ?? '',
  };
  return map[sender.token ?? ''] ?? '';
}

/**
 * The full merge-token vocabulary for one member, resolved against a
 * campaign + sender — extracted from `interpolateTemplate`'s own inline
 * `vars` map (Launcher V2 C2, plan `2026-09-01-001`) so both it and
 * `interpolateTemplateSegments` resolve against exactly one source of truth
 * instead of two copies that can drift.
 */
export function buildTemplateVars(
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): Record<string, string> {
  const nameParts = (member.contact_name ?? '').trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const senderEmail = resolveSenderEmail(sender, member);
  const senderFirstName = resolveSenderFirstName(sender, member);
  const senderMeetingLink = resolveSenderMeetingLink(sender, member);
  const eventDate = campaign?.event_date ?? campaign?.start_date ?? '';

  return {
    'First Name':           firstName,
    'Last Name':            lastName,
    'Full Name':            member.contact_name ?? '',
    'Email':                member.contact_email ?? '',
    'Title':                member.contact_title ?? '',
    'Campaign Status':      member.cm_status ?? '',
    'Owner Role':           member.owner_role ?? '',
    'Fit Score':            String(member.fit_score ?? ''),
    'Account Name':         member.account_name ?? '',
    'Sales Tier':           member.sales_tier ?? '',
    'GTME Owner Name':       member.gtme_owner_name ?? '',
    'GTME Owner First Name': member.gtme_owner_first_name ?? member.gtme_owner_name?.split(' ')[0] ?? '',
    'GTME Owner Email':     member.gtme_owner_email ?? '',
    'GTME Meeting Link':    member.gtme_meeting_link ?? '',
    'GS Owner Name':        member.gs_owner_name ?? '',
    'GS Owner First Name':  member.gs_owner_first_name ?? member.gs_owner_name?.split(' ')[0] ?? '',
    'GS Owner Email':       member.gs_owner_email ?? '',
    'GS Meeting Link':      member.gs_meeting_link ?? '',
    'ClayDR Name':          member.claydr_name ?? '',
    'ClayDR First Name':    member.claydr_first_name ?? member.claydr_name?.split(' ')[0] ?? '',
    'ClayDR Email':         member.claydr_email ?? '',
    'ClayDR Meeting Link':  member.claydr_meeting_link ?? '',
    'Scanner First Name':   member.primary_met_with_first_name ?? member.primary_met_with_name?.split(' ')[0] ?? '',
    'Scanner Email':        member.primary_met_with_email ?? '',
    'Primary Seller Link':  member.gtme_meeting_link || member.claydr_meeting_link || '',
    'Event Name':           campaign?.name ?? '',
    'Event Date':           eventDate,
    'Campaign Type':        campaign?.type ?? '',
    'Sender First Name':    senderFirstName,
    'Sender Email':         senderEmail ?? '',
    'Sender Meeting Link':  senderMeetingLink,
  };
}

/** Own-key check without `hasOwnProperty.call` (ES2020 lib; keeps the shared-code linter quiet). */
function hasTemplateVar(vars: Record<string, string>, key: string): boolean {
  return Object.getOwnPropertyDescriptor(vars, key) !== undefined;
}

export function interpolateTemplate(
  template: string,
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): string {
  const vars = buildTemplateVars(member, campaign, sender);

  return template.replace(/\{\{([^}]+)\}\}/g, (_match, key) => {
    const trimmed = key.trim();
    return hasTemplateVar(vars, trimmed) ? vars[trimmed] : `{{${trimmed}}}`;
  });
}

/**
 * One rendered piece of a template: literal text, a resolved value, a
 * known-but-empty token, or a token outside the vocabulary entirely.
 *
 * `empty` and `unknown` both carry the raw `{{Token}}` text (never a blank
 * string) so a caller can render it as a visible, styled chip instead of
 * silently dropping it — matching the prototype's `interpolate()`
 * (`playbooks-prototype-v6.html:3571-3577`).
 */
export type TemplateSegment =
  | { kind: 'text'; text: string }
  | { kind: 'filled'; text: string }
  | { kind: 'empty'; text: string }
  | { kind: 'unknown'; text: string };

/**
 * `interpolateTemplate`'s segment-producing sibling (Launcher V2 C2, plan
 * `2026-09-01-001`) — same vocabulary (`buildTemplateVars`), but reports
 * which parts of the template are plain text vs. a resolved token vs. a
 * known-but-empty token vs. an unrecognized token, instead of collapsing
 * everything into one string. `interpolateTemplate` itself is untouched by
 * this addition — existing v1 consumers keep getting a plain string.
 */
export function interpolateTemplateSegments(
  template: string,
  member: CampaignMember,
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): TemplateSegment[] {
  const vars = buildTemplateVars(member, campaign, sender);
  const segments: TemplateSegment[] = [];
  const pattern = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(template)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', text: template.slice(lastIndex, match.index) });
    }
    const trimmed = match[1].trim();
    if (!hasTemplateVar(vars, trimmed)) {
      segments.push({ kind: 'unknown', text: `{{${trimmed}}}` });
    } else if (vars[trimmed] === '') {
      segments.push({ kind: 'empty', text: `{{${trimmed}}}` });
    } else {
      segments.push({ kind: 'filled', text: vars[trimmed] });
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < template.length) {
    segments.push({ kind: 'text', text: template.slice(lastIndex) });
  }

  return segments;
}

// ── Template variable gap detection ──────────────────────────────────────────

// Variables resolved downstream by the Clay prompt — not our responsibility
const CLAY_RESOLVED_VARS = new Set([
  'First Name',
  'Company Name',
  'Sender First Name',
  'Sender Meeting Link',
]);

/**
 * The `{{Token}}` names this copy actually uses, minus the ones Clay resolves
 * downstream. Extracted ONCE per copy — the whole point of
 * {@link checkCopyVarGapsByMemberId}'s existence (see its doc).
 *
 * A fresh `RegExp` per call rather than a module-scope one: a `/g` regex
 * carries mutable `lastIndex`, so sharing one instance across calls (or
 * across the two `exec` loops below) would make the scan order-dependent.
 */
function usedTemplateVars(subject: string, body: string): Set<string> {
  const usedVars = new Set<string>();
  const varPattern = /\{\{([^}]+)\}\}/g;
  for (const text of [subject, body]) {
    varPattern.lastIndex = 0;
    let m;
    while ((m = varPattern.exec(text)) !== null) {
      const trimmed = m[1].trim();
      if (!CLAY_RESOLVED_VARS.has(trimmed)) usedVars.add(trimmed);
    }
  }
  return usedVars;
}

/**
 * Playbooks QA round 4, U11 (E1) / Decision 14 — the gap check keyed by
 * `member.id`, and the primitive {@link checkCopyVarGaps} is now built on.
 *
 * ── WHY (the E1 typing lag) ───────────────────────────────────────────────
 * `mini-apps/playbooks/copy-token-flags.ts#rosterTokenGaps` needs
 * collision-free `memberIds` (two members can share a display name), which
 * `checkCopyVarGaps`' `memberNames` aggregation cannot give it. So it used to
 * call `checkCopyVarGaps` ONCE PER MEMBER with a one-element array — and
 * every one of those calls re-ran the `{{...}}` regex over the whole joined
 * subject+body. On a 400-member roster that is 400 full scans of the copy per
 * keystroke: O(M·len) work in the keystroke's own commit path.
 *
 * This extracts `usedVars` once and then loops members: O(len + M·vars).
 * Returns `variable -> memberIds[]`, in roster order within each variable, so
 * the caller's shape transform is a plain `map`.
 *
 * An empty map is returned (with NO member loop at all) when the copy uses no
 * tokens — the common case while a rep is typing prose.
 */
export function checkCopyVarGapsByMemberId(
  subject: string,
  body: string,
  members: readonly CampaignMember[],
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): Map<string, string[]> {
  const gapMemberIds = new Map<string, string[]>();
  const usedVars = usedTemplateVars(subject, body);
  if (usedVars.size === 0) return gapMemberIds;

  for (const member of members) {
    // Same vocabulary as interpolation — one source of truth, so a token the
    // picker offers can never be "present" here but silently unfilled there.
    const vars = buildTemplateVars(member, campaign, sender);
    for (const v of usedVars) {
      if (!(vars[v] ?? '')) {
        const ids = gapMemberIds.get(v);
        if (ids) ids.push(member.id);
        else gapMemberIds.set(v, [member.id]);
      }
    }
  }
  return gapMemberIds;
}

/**
 * Lightweight gap check for a single cell's draft copy against a set of
 * members, aggregated by DISPLAY NAME.
 *
 * U11 (E1) — rebuilt as a shape transform over
 * {@link checkCopyVarGapsByMemberId} rather than its own loop, so the two can
 * never disagree about what counts as a gap. The return shape
 * (`{variable, count, memberNames}[]`, insertion-ordered by first-seen
 * variable, `memberNames` in roster order, falling back to email then
 * `'Unknown'`) is UNCHANGED — its callers (`stepTokenGaps`, the preview
 * cards) are untouched by that unit.
 */
export function checkCopyVarGaps(
  subject: string,
  body: string,
  members: CampaignMember[],
  campaign: { name?: string; event_date?: string | null; start_date?: string | null; type?: string } | null,
  sender: SenderValue | null,
): { variable: string; count: number; memberNames: string[] }[] {
  const nameById = new Map(
    members.map((member) => [member.id, member.contact_name ?? member.contact_email ?? 'Unknown'] as const),
  );
  return Array.from(checkCopyVarGapsByMemberId(subject, body, members, campaign, sender).entries())
    .filter(([, memberIds]) => memberIds.length > 0)
    .map(([variable, memberIds]) => ({
      variable,
      count: memberIds.length,
      // `?? 'Unknown'` is unreachable (every id came from `members`) but
      // keeps this a total function rather than emitting `undefined` into a
      // `string[]` if a future caller ever passes duplicate ids.
      memberNames: memberIds.map((id) => nameById.get(id) ?? 'Unknown'),
    }));
}
